import { useEffect, useRef } from "react";
import {
  useFieldArray,
  UseFieldArrayReturn,
  useFormContext,
} from "react-hook-form";

interface Props {
  id: string;
  list: any[];
}
export interface IField {
  id: string;
  text?: string;
  value?: string;
  time?: string;
  date?: string;
}
interface ReturnProps {
  fields: IField[];
  handleAdd: () => void;
  handleRemove: (index: number) => () => void;
  updateField: (
    index: number,
    newData: Partial<IField>,
    manual?: boolean,
  ) => void;
}
/**
 * Custom hook to manage a list of input fields with React Hook Form.
 *
 * @param props - An object containing the following properties:
 *   - id: A string representing the name of the field array in the form.
 *   - list: An array of initial field data to populate the input list.
 * @returns An object containing the following:
 *   - fields: An array of field objects managed by the hook.
 *   - handleAdd: A function to add a new field to the list.
 *   - handleRemove: A function to remove a field from the list by index.
 *   - updateField: A function to update a specific field in the list by index.
 */
const useInputListController = ({ list, id }: Props): ReturnProps => {
  const { control, getValues, setValue } = useFormContext();
  const { fields, append, replace, remove } = useFieldArray({
    name: id,
    control,
  }) as UseFieldArrayReturn;
  const initializedRef = useRef(false); // Use a ref to track initialization

  useEffect(() => {
    if (list?.length > 0 && !initializedRef.current) {
      replace(list);
      initializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, []);

  const handleAdd = () => {
    initializedRef.current = true;
    const newItem = { id: "new", text: "", value: "", type: "" };
    append(newItem);
  };

  const handleRemove = (index: number) => () => {
    initializedRef.current = true;
    remove(index);
  };
  const updateField = (
    index: number,
    newData: Partial<any>,
    manual?: boolean,
  ) => {
    const current = getValues(id); // id is the name of your field array
    const updated = [...current];
    if (updated[index].time && updated[index].date && !manual) {
      newData = {
        ...newData,
        time: updated[index].time,
        date: updated[index].date,
      };
    }
    updated[index] = {
      ...updated[index],
      ...newData,
    };

    setValue(id, updated); // This updates the field array and triggers UI update
  };
  return { fields, handleAdd, handleRemove, updateField };
};

export default useInputListController;
